/**
 * Machine → React bridge.
 *
 * The @atlassian/state-machine machine is the single source of truth for a flow's phase
 * (`machine.state()`). But the machine is not React-aware, and its
 * `onTransition(listener)` registers a listener that can only be removed by
 * `destroy()`-ing the whole machine (there is no per-listener unsubscribe).
 * `useSyncExternalStore` requires a `subscribe(cb) => unsubscribe` contract, so
 * this module provides that missing piece.
 *
 * Design:
 * - We register exactly ONE `onTransition` listener per machine (a "fan-out"),
 *   and maintain our own `Set` of React subscriber callbacks that we can add to
 *   and remove from individually. That gives real unsubscribe semantics on top
 *   of the lib's append-only listener list.
 * - There is also a set of GLOBAL subscribers (for aggregate reads like
 *   "is any remix in progress"), notified on every machine's transition and on
 *   registry membership changes (start/end of a flow).
 *
 * With this bridge:
 * - A late-mounting component reads `machine.state()` synchronously on mount
 *   (correct initial value — no missed-command problem).
 * - A mounted component re-renders on the next transition (subscription).
 *
 * Both fall out of one `useSyncExternalStore` hook (see `useMachineState.ts`);
 * no separate reactive mirror of the phase is required.
 *
 * Additive; not wired into live flows.
 */

import type { RemixMachine } from './remixMachine';

/** Per-machine React subscribers, keyed by remix id. */
const perMachineSubscribers = new Map<string, Set<() => void>>();

/** Machines we have already attached our single fan-out listener to. */
const bridgedMachines = new Set<string>();

/** Global subscribers (aggregate reads across all flows). */
const globalSubscribers = new Set<() => void>();

const notify = (subscribers: Set<() => void> | undefined): void => {
	if (!subscribers) {
		return;
	}
	// Copy before iterating: a subscriber may unsubscribe during notification.
	for (const cb of Array.from(subscribers)) {
		cb();
	}
};

/**
 * Register a machine with the bridge. Attaches a single `onTransition` fan-out
 * that notifies this machine's subscribers and all global subscribers. Idempotent
 * per id. Call this when a flow's machine is created.
 */
export const registerMachineWithBridge = (id: string, machine: RemixMachine): void => {
	if (bridgedMachines.has(id)) {
		return;
	}
	bridgedMachines.add(id);
	machine.onTransition(() => {
		notify(perMachineSubscribers.get(id));
		notify(globalSubscribers);
	});
	// Membership changed → aggregate reads (e.g. isAnyRemixing) must re-evaluate.
	notify(globalSubscribers);
};

/**
 * Unregister a machine from the bridge. Call this when a flow ends (its machine
 * is destroyed). Drops per-machine subscribers and notifies global subscribers
 * that membership changed.
 */
export const unregisterMachineFromBridge = (id: string): void => {
	perMachineSubscribers.delete(id);
	bridgedMachines.delete(id);
	notify(globalSubscribers);
};

/**
 * Subscribe to a single machine's transitions. Returns a real unsubscribe.
 * Used by `useSyncExternalStore` for per-flow reads (phase, instance).
 */
export const subscribeToMachine = (id: string, callback: () => void): (() => void) => {
	let set = perMachineSubscribers.get(id);
	if (!set) {
		set = new Set();
		perMachineSubscribers.set(id, set);
	}
	set.add(callback);
	return () => {
		set?.delete(callback);
	};
};

/**
 * Subscribe to changes across all flows: any machine transition, or any flow
 * starting/ending. Returns a real unsubscribe. Used for aggregate reads such as
 * `useIsAnyRemixing`.
 */
export const subscribeToAnyMachine = (callback: () => void): (() => void) => {
	globalSubscribers.add(callback);
	return () => {
		globalSubscribers.delete(callback);
	};
};

/** Test helper: clear all bridge bookkeeping. */
export const __resetBridge = (): void => {
	perMachineSubscribers.clear();
	bridgedMachines.clear();
	globalSubscribers.clear();
};
